import { useWatch } from 'react-hook-form';
import { Alert, Box, Grid, Typography } from '@mui/material';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import SectionHeader from '../../../components/shared/SectionHeader';
import SelectField from '../../../components/form/SelectField';
import CurrencyField from '../../../components/form/CurrencyField';
import NumberField from '../../../components/form/NumberField';

const PURPOSE_OPTIONS = [
  { value: 'Purchase', label: 'Purchase' },
  { value: 'Refinance', label: 'Refinance (Rate/Term)' },
  { value: 'Cash-Out Refinance', label: 'Cash-Out Refinance' },
];

const LOAN_TYPE_OPTIONS = [
  { value: 'Conventional', label: 'Conventional' },
  { value: 'FHA', label: 'FHA' },
  { value: 'VA', label: 'VA' },
  { value: 'USDA', label: 'USDA' },
  { value: 'Jumbo', label: 'Jumbo' },
];

const REFI_PURPOSE_OPTIONS = [
  { value: 'Home Improvement', label: 'Home Improvement' },
  { value: 'Debt Consolidation', label: 'Debt Consolidation' },
  { value: 'Education', label: 'Education' },
  { value: 'Investment', label: 'Investment' },
  { value: 'Other', label: 'Other' },
];

const LoanDetailsStep = () => {
  const [loanPurpose, loanAmount, propertyValue] = useWatch({
    name: ['loanDetails.loanPurpose', 'loanDetails.loanAmount', 'property.propertyValue'],
  });

  const isRefi = loanPurpose === 'Refinance' || loanPurpose === 'Cash-Out Refinance';
  const isCashOut = loanPurpose === 'Cash-Out Refinance';

  const ltv =
    propertyValue > 0 && loanAmount > 0
      ? ((loanAmount / propertyValue) * 100).toFixed(1)
      : null;
  const ltvNum = ltv != null ? parseFloat(ltv) : null;
  const ltvSeverity =
    ltvNum == null ? 'info' : ltvNum <= 80 ? 'success' : ltvNum <= 95 ? 'warning' : 'error';

  return (
    <Box>
      <SectionHeader title="Loan Details" icon={<RequestQuoteIcon />} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isRefi
          ? 'Tell us about your current mortgage and refinance goals'
          : 'Loan purpose, amount, and type'}
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <SelectField name="loanDetails.loanPurpose" label="Loan Purpose" required options={PURPOSE_OPTIONS} size="small" />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <SelectField name="loanDetails.loanType" label="Loan Type" options={LOAN_TYPE_OPTIONS} size="small" />
        </Grid>

        {!isRefi && (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <CurrencyField name="loanDetails.loanAmount" label="Loan Amount" size="small" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <CurrencyField name="loanDetails.downPayment" label="Down Payment" size="small" />
            </Grid>
          </>
        )}

        {isRefi && (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <CurrencyField name="loanDetails.currentMortgageBalance" label="Current Mortgage Balance" size="small" />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <NumberField
                name="loanDetails.currentInterestRate"
                label="Current Interest Rate (%)"
                fullWidth
                size="small"
                step={0.001}
              />
            </Grid>
            {isCashOut && (
              <Grid size={{ xs: 12, md: 6 }}>
                <CurrencyField name="loanDetails.cashOutAmount" label="Cash-Out Amount" size="small" />
              </Grid>
            )}
            <Grid size={{ xs: 12, md: 6 }}>
              <CurrencyField name="loanDetails.loanAmount" label="New Loan Amount" size="small" />
            </Grid>
            {isCashOut && (
              <Grid size={{ xs: 12, md: 6 }}>
                <SelectField
                  name="loanDetails.refinancePurpose"
                  label="Refinance Purpose"
                  options={REFI_PURPOSE_OPTIONS}
                  size="small"
                />
              </Grid>
            )}
          </>
        )}
      </Grid>

      {ltv && (
        <Alert severity={ltvSeverity} sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Loan-to-Value Ratio
          </Typography>
          <Typography variant="h4" component="p">
            {ltv}%
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {isRefi
              ? ltvNum <= 80
                ? 'Good equity position for refinancing'
                : ltvNum <= 95
                  ? 'May need PMI on the new loan'
                  : 'Very high LTV — refinance options may be limited'
              : ltvNum <= 80
                ? 'No private mortgage insurance (PMI) required'
                : ltvNum <= 95
                  ? 'PMI will likely be required'
                  : 'Very high LTV — limited loan options available'}
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default LoanDetailsStep;
